import { useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import { GetUserAppointmentGroupingsQuery, useGetUserAppointmentGroupingsQuery } from 'shared/types/graphql';

interface UseGetUserAppointmentGroupsProps {
  numberOfGroups: number;
  numberOfAppointmentsPerPage?: number;
  filteredAppointmentGroupings?: string[];
  clinicId: string;
  userId: string;
}

interface UseGetUserAppointmentGroupsReturn {
  groupPage: number;
  setGroupPage: (page: number) => void;
  totalGroups?: number;
  userAppointmentGroupings: GetUserAppointmentGroupingsQuery['userAppointmentGroupings'];
  refetchUserAppointmentGroupings: () => void;
  isLoadingAppointmentGroups: boolean;
}

export function useGetUserAppointmentGroupings({
  numberOfGroups = 4,
  clinicId,
  filteredAppointmentGroupings,
  userId,
}: UseGetUserAppointmentGroupsProps): UseGetUserAppointmentGroupsReturn {
  const defaultGroup: NonNullable<GetUserAppointmentGroupingsQuery['userAppointmentGroupings']>[0] = {
    id: '_all',
    clinicId,
    label: 'All',
    clinicEmployees: [],
    rooms: [],
    appointmentTypes: [],
  };
  const [groupPage, setGroupPage] = useState(1);
  const isFilteringGroups = useMemo(() => !!filteredAppointmentGroupings?.length, [filteredAppointmentGroupings]);

  const {
    data: userAppointmentGroupingsData,
    refetch: refetchUserAppointmentGroupings,
    loading: isLoadingAppointmentGroups,
  } = useGetUserAppointmentGroupingsQuery({
    variables: {
      where: {
        clinicId: { equals: clinicId },
        userId: { equals: userId },
        ...(isFilteringGroups && { id: { in: filteredAppointmentGroupings } }),
      },
      take: numberOfGroups,
      skip: (groupPage - 1) * numberOfGroups,
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !clinicId || !userId,
  });

  return {
    groupPage,
    setGroupPage,
    refetchUserAppointmentGroupings,
    totalGroups: userAppointmentGroupingsData?.count ?? 0, //we add an all group for every user
    userAppointmentGroupings: [defaultGroup, ...(userAppointmentGroupingsData?.userAppointmentGroupings || [])],
    isLoadingAppointmentGroups,
  };
}
